import { StatusBadge, cn, type StatusBadgeTone } from '@immo/ui-kit';
import type { FleetMachineApproval, FleetProfileState } from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';

const dashedBadgeClass =
  'inline-flex h-5 items-center whitespace-nowrap rounded-full border border-dashed border-neutral-300 px-2 text-sm font-medium text-text-placeholder';

const STATE_TONES: Record<
  Exclude<FleetProfileState, 'pending'>,
  { readonly tone: StatusBadgeTone; readonly dot: boolean }
> = {
  working: { tone: 'success', dot: true },
  idle: { tone: 'info', dot: true },
  'waiting-on-human': { tone: 'warning', dot: true },
  // No dot — the one visual distinction that keeps a stopped profile from
  // reading as a live one (the design's "stopped ≠ stale" AC).
  stopped: { tone: 'neutral', dot: false },
};

const APPROVAL_TONES: Record<Exclude<FleetMachineApproval, 'pending'>, StatusBadgeTone> = {
  approved: 'success',
  rejected: 'danger',
  revoked: 'neutral',
};

/** The five `state` tokens (ADR 0047) — the only agent-reported field this page translates. */
export function FleetStateBadge({ state }: { readonly state: FleetProfileState }) {
  const t = useTranslations('pages.admin.fleet.state');

  if (state === 'pending') {
    return <span className={dashedBadgeClass}>{t('pending')}</span>;
  }

  const { tone, dot } = STATE_TONES[state];

  return <StatusBadge label={t(state)} tone={tone} dot={dot} />;
}

/** The rail's compact state marker — same five tokens, dot-only. */
export function FleetStateDot({ state }: { readonly state: FleetProfileState }) {
  if (state === 'pending') {
    return <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full border border-dashed border-neutral-400" />;
  }

  const dotClass: Record<Exclude<FleetProfileState, 'pending'>, string> = {
    working: 'bg-success-500',
    idle: 'bg-info-500',
    'waiting-on-human': 'bg-warning-500',
    stopped: 'bg-neutral-400',
  };

  return <span aria-hidden className={cn('h-1.5 w-1.5 shrink-0 rounded-full', dotClass[state])} />;
}

/** The four `approval` tokens (ADR 0047). */
export function FleetApprovalBadge({ approval }: { readonly approval: FleetMachineApproval }) {
  const t = useTranslations('pages.admin.fleet.approval');

  if (approval === 'pending') {
    return <span className={dashedBadgeClass}>{t('pending')}</span>;
  }

  return <StatusBadge label={t(approval)} tone={APPROVAL_TONES[approval]} />;
}

const CIRCLE_FILL: Record<Exclude<FleetProfileState, 'pending'>, string> = {
  // The ring marks the one state that is doing something right now.
  working: 'bg-success-500 ring-[3px] ring-success-100',
  idle: 'bg-info-500',
  'waiting-on-human': 'bg-warning-500',
  stopped: 'bg-neutral-400',
};

/**
 * The lane tile's state circle (OPS-67) — the same five tokens as the dot,
 * large enough to scan a fleet by. `pending` is an outline: nothing has been
 * reported yet, so nothing is filled in.
 */
export function FleetStateCircle({
  state,
  className,
}: {
  readonly state: FleetProfileState;
  readonly className?: string;
}) {
  if (state === 'pending') {
    return (
      <span
        aria-hidden
        className={cn('h-3 w-3 shrink-0 rounded-full border-[1.5px] border-dashed border-neutral-400', className)}
      />
    );
  }

  return <span aria-hidden className={cn('h-3 w-3 shrink-0 rounded-full', CIRCLE_FILL[state], className)} />;
}

/** The text colour a state label takes on a lane tile — the badge's own foreground. */
export const STATE_LABEL_CLASS: Record<FleetProfileState, string> = {
  working: 'text-success-700',
  idle: 'text-info-700',
  'waiting-on-human': 'text-warning-700',
  stopped: 'text-text-tertiary',
  pending: 'text-text-placeholder',
};
